/**
 * Authenticated API Client
 *
 * This utility provides an authenticated fetch wrapper that automatically:
 * - Adds JWT authorization headers to requests
 * - Handles token refresh on 401 errors
 * - Manages authentication state updates
 * - Provides seamless API access for authenticated requests
 * - Optional retry logic with exponential backoff
 */

import useAuthStore from '../stores/authStore';

// Base API URL - adjust based on your backend configuration
// NOTE: Must use 127.0.0.1 instead of localhost (CLAUDE.md requirement)
// When REACT_APP_API_URL is explicitly set to '' (Cloud Run: same-domain), use ''.
// When not set at all (local dev), fallback to http://127.0.0.1:8080.
const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : 'http://127.0.0.1:8080';

// Base path for login redirect, derived from PUBLIC_URL (set by homepage in package.json)
// e.g. "/ai" in both dev and production, avoids hardcoding the basename
const LOGIN_PATH = `${process.env.PUBLIC_URL || ''}/login`;

// Global flag to prevent multiple simultaneous redirects to login
let isRedirectingToLogin = false;

const isWeComAppBrowser = (): boolean => /wxwork/i.test(window.navigator.userAgent);

/**
 * When a user is inside the WeCom app, a failed local refresh should first
 * attempt one fresh OAuth authorization. This keeps the existing Prism and
 * other sub-system return flow seamless without weakening JWT validation.
 */
const tryWeComAutoReauth = async (): Promise<boolean> => {
  if (!isWeComAppBrowser()) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/wecom/start?mode=app`, {
      credentials: 'include',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.auth_url) return false;

    await useAuthStore.getState().logout();
    window.location.replace(data.auth_url);
    return true;
  } catch {
    return false;
  }
};

type AuthenticatedFetchOptions = RequestInit & {
  loginRedirectPath?: string | null;
};

const buildLoginRedirectUrl = (nextPath?: string | null): string => {
  if (!nextPath || !nextPath.startsWith('/') || nextPath.startsWith('//')) {
    return LOGIN_PATH;
  }
  return `${LOGIN_PATH}?next=${encodeURIComponent(nextPath)}`;
};

/**
 * Helper function to perform logout and redirect to login page
 * Prevents multiple simultaneous redirects
 */
const logoutAndRedirect = async (nextPath?: string | null): Promise<void> => {
  if (isRedirectingToLogin) {
    return;
  }

  isRedirectingToLogin = true;
  if (await tryWeComAutoReauth()) {
    return;
  }
  const { logout } = useAuthStore.getState();
  await logout();

  // Use a small delay to ensure state is updated before redirecting
  setTimeout(() => {
    window.location.href = buildLoginRedirectUrl(nextPath);
    // Reset flag after redirect
    setTimeout(() => {
      isRedirectingToLogin = false;
    }, 1000);
  }, 100);
};

/**
 * Authenticated fetch wrapper that automatically handles JWT tokens
 * @param url - The API endpoint URL (relative or absolute)
 * @param options - Fetch options (method, headers, body, etc.)
 * @returns The fetch response
 */
export const authenticatedFetch = async (
  url: string,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> => {
  const { loginRedirectPath, ...fetchOptions } = options;
  // Get auth state and methods from the store
  const { accessToken, refreshToken, refreshAccessToken } = useAuthStore.getState();

  // Ensure we have an access token
  if (!accessToken) {
    throw new Error('No access token available. Please log in.');
  }

  // Prepare the full URL
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  // Prepare headers with authentication
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers || {}),
    'Authorization': `Bearer ${accessToken}`
  };

  // Make the initial request
  let response = await fetch(fullUrl, {
    ...fetchOptions,
    headers
  });

  // If we get a 401 (Unauthorized), try to refresh the token
  if (response.status === 401 && refreshToken) {
    try {
      // Attempt to refresh the access token
      const refreshSuccess = await refreshAccessToken();

      if (refreshSuccess) {
        // Get the new access token and retry the request
        const { accessToken: newAccessToken } = useAuthStore.getState();

        const newHeaders: HeadersInit = {
          ...headers,
          'Authorization': `Bearer ${newAccessToken}`
        };

        // Retry the original request with the new token
        response = await fetch(fullUrl, {
          ...fetchOptions,
          headers: newHeaders
        });
      } else {
        // Refresh failed, logout and redirect
        await logoutAndRedirect(loginRedirectPath);
        throw new Error('Authentication failed. Please log in again.');
      }
    } catch (error) {
      // Refresh failed, logout and redirect
      await logoutAndRedirect(loginRedirectPath);
      throw new Error('Authentication failed. Please log in again.');
    }
  }

  return response;
};

/**
 * Convenience method for authenticated GET requests
 * @param url - The API endpoint URL
 * @param options - Additional fetch options
 * @returns The fetch response
 */
export const authenticatedGet = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  return authenticatedFetch(url, {
    method: 'GET',
    ...options
  });
};

/**
 * Convenience method for authenticated POST requests
 * @param url - The API endpoint URL
 * @param data - The data to send in the request body
 * @param options - Additional fetch options
 * @returns The fetch response
 */
export const authenticatedPost = async (
  url: string,
  data: any = null,
  options: RequestInit = {}
): Promise<Response> => {
  return authenticatedFetch(url, {
    method: 'POST',
    body: data ? JSON.stringify(data) : null,
    ...options
  });
};

/**
 * Convenience method for authenticated PUT requests
 * @param url - The API endpoint URL
 * @param data - The data to send in the request body
 * @param options - Additional fetch options
 * @returns The fetch response
 */
export const authenticatedPut = async (
  url: string,
  data: any = null,
  options: RequestInit = {}
): Promise<Response> => {
  return authenticatedFetch(url, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : null,
    ...options
  });
};

/**
 * Convenience method for authenticated DELETE requests
 * @param url - The API endpoint URL
 * @param options - Additional fetch options
 * @returns The fetch response
 */
export const authenticatedDelete = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  return authenticatedFetch(url, {
    method: 'DELETE',
    ...options
  });
};

/**
 * Helper function to handle API responses with automatic error handling
 * @param responsePromise - The fetch response promise
 * @returns The parsed JSON response
 * @throws If the response is not ok or parsing fails
 */
export const handleApiResponse = async (responsePromise: Promise<Response>): Promise<any> => {
  try {
    const response = await responsePromise;

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        // Handle nested error structures
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (typeof errorData.detail === 'object' && errorData.detail.message) {
            errorMessage = errorData.detail.message;
          } else {
            errorMessage = JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          if (typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else {
            errorMessage = JSON.stringify(errorData.message);
          }
        }
      } catch (parseError) {
        // If we can't parse the error response, use the status text
      }

      // For 401 errors, trigger logout and redirect to login page
      if (response.status === 401) {
        await logoutAndRedirect();
        throw new Error('Session expired. Please log in again.');
      }

      throw new Error(errorMessage);
    }

    // Try to parse JSON response
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    // Return response text for non-JSON responses
    return await response.text();

  } catch (error) {
    throw error;
  }
};

/**
 * Unauthenticated fetch for public endpoints (login, register, etc.)
 * @param url - The API endpoint URL
 * @param options - Fetch options
 * @returns The fetch response
 */
export const publicFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };

  return fetch(fullUrl, {
    ...options,
    headers
  });
};

const apiClient = {
  authenticatedFetch,
  authenticatedGet,
  authenticatedPost,
  authenticatedPut,
  authenticatedDelete,
  handleApiResponse,
  publicFetch
};

export default apiClient;
